// Game functionality for OSI Model Interactive Presentation

// Game data
const gameCases = [
    {
        description: "A student is trying to connect to the school Wi-Fi network but can't get online. Their laptop shows it's connected to the network, but web pages won't load. Other students are using the same network without issues.",
        correctLayer: 3,
        explanation: "This is a Network Layer (Layer 3) problem. The student's device has successfully connected to the Wi-Fi network (Layer 2 is working), but can't route traffic to the internet. This could be due to an incorrect IP address, DNS configuration issue, or a routing problem.",
        badge: "Network Navigator"
    },
    {
        description: "During a video call with a classmate, you can hear them clearly, but the video keeps freezing and breaking up. Your internet speed test shows good download and upload speeds.",
        correctLayer: 4,
        explanation: "This is a Transport Layer (Layer 4) problem. The issue involves the reliable delivery of data packets. TCP (Transmission Control Protocol) at Layer 4 handles flow control and packet ordering. When video freezes while audio works, it's often because of packet loss or out-of-order delivery affecting the larger video data stream.",
        badge: "Transport Troubleshooter"
    },
    {
        description: "A student tries to open a website, but gets an error saying the certificate is invalid or untrusted. The website address starts with 'https://'.",
        correctLayer: 6,
        explanation: "This is a Presentation Layer (Layer 6) problem. SSL/TLS certificates are handled at the Presentation Layer, which is responsible for encryption, decryption, and data format conversion. An invalid certificate means there's an issue with the security protocols that encrypt the connection.",
        badge: "Security Specialist"
    },
    {
        description: "A student plugged an Ethernet cable into their laptop, but the network icon still shows no connection. When they look at the port on their laptop, no lights are blinking.",
        correctLayer: 1,
        explanation: "This is a Physical Layer (Layer 1) problem. The issue is with the physical connection itself - the cable might be damaged, the port might be broken, or the network interface card might not be functioning. The absence of lights on the network port indicates no electrical signal is being detected.",
        badge: "Hardware Hero"
    },
    {
        description: "A student can browse most websites, but when trying to use a specific application that requires logging in, the connection fails after entering credentials.",
        correctLayer: 5,
        explanation: "This is a Session Layer (Layer 5) problem. The Session Layer establishes, maintains, and terminates connections between applications. In this case, the session establishment is failing after authentication. This could be due to session timeout settings, session management issues, or authentication problems.",
        badge: "Session Solver"
    }
];

// Game variables
let currentCaseIndex = 0;
let score = 0;
let earnedBadges = [];

// DOM Elements
const scoreElement = document.getElementById('score');
const currentCaseElement = document.getElementById('current-case');
const totalCasesElement = document.getElementById('total-cases');
const caseDescriptionElement = document.getElementById('case-description');
const layerOptions = document.querySelectorAll('.layer-option');
const feedbackContainer = document.getElementById('feedback-container');
const nextCaseBtn = document.getElementById('next-case');
const badgesContainer = document.getElementById('badges-container');
const finalScoreElement = document.getElementById('final-score');
const maxScoreElement = document.getElementById('max-score');
const finalBadgesContainer = document.getElementById('final-badges-container');
const restartGameBtn = document.getElementById('restart-game');

// Initialize the game
function startGame() {
    // Reset game variables
    currentCaseIndex = 0;
    score = 0;
    earnedBadges = [];
    
    // Update UI
    scoreElement.textContent = score;
    currentCaseElement.textContent = currentCaseIndex + 1;
    totalCasesElement.textContent = gameCases.length;
    badgesContainer.innerHTML = '';
    
    // Load first case
    loadCase(currentCaseIndex);
    
    // Add event listeners to layer options
    layerOptions.forEach(option => {
        option.addEventListener('click', handleLayerSelection);
    });
    
    // Add event listener to next case button
    nextCaseBtn.addEventListener('click', loadNextCase);
    
    // Add event listener to restart button
    restartGameBtn.addEventListener('click', restartGame);
}

// Load a case
function loadCase(index) {
    const currentCase = gameCases[index];
    
    // Update case description
    caseDescriptionElement.innerHTML = `
        <h4>Case ${index + 1}: Network Problem</h4>
        <p>${currentCase.description}</p>
    `;
    
    // Reset feedback container
    feedbackContainer.style.display = 'none';
    
    // Reset layer options
    layerOptions.forEach(option => {
        option.style.backgroundColor = '';
        option.style.color = '';
        option.disabled = false;
    });
    
    // Disable next case button until an answer is selected
    nextCaseBtn.disabled = true;
}

// Handle layer selection
function handleLayerSelection() {
    const selectedLayer = parseInt(this.getAttribute('data-layer'));
    const currentCase = gameCases[currentCaseIndex];
    
    // Disable all options after selection
    layerOptions.forEach(option => {
        option.disabled = true;
    });
    
    // Check if answer is correct
    const isCorrect = selectedLayer === currentCase.correctLayer;
    
    // Update score
    if (isCorrect) {
        score++;
        scoreElement.textContent = score;
        
        // Add badge if not already earned
        if (!earnedBadges.includes(currentCase.badge)) {
            earnedBadges.push(currentCase.badge);
            addBadge(currentCase.badge, currentCase.correctLayer);
        }
    }
    
    // Highlight selected option
    this.style.backgroundColor = isCorrect ? 'var(--secondary-color)' : 'var(--accent-color)';
    this.style.color = 'white';
    
    // Highlight correct option if wrong answer was selected
    if (!isCorrect) {
        layerOptions[currentCase.correctLayer - 1].style.backgroundColor = 'var(--secondary-color)';
        layerOptions[currentCase.correctLayer - 1].style.color = 'white';
    }
    
    // Show feedback
    feedbackContainer.innerHTML = `
        <h4 class="${isCorrect ? 'correct-feedback' : 'incorrect-feedback'}">
            ${isCorrect ? 'Correct!' : 'Not quite right.'}
        </h4>
        <p>${currentCase.explanation}</p>
    `;
    feedbackContainer.style.display = 'block';
    
    // Enable next case button
    nextCaseBtn.disabled = false;
}

// Load next case
function loadNextCase() {
    currentCaseIndex++;
    
    // Check if game is over
    if (currentCaseIndex >= gameCases.length) {
        endGame();
        return;
    }
    
    // Update current case indicator
    currentCaseElement.textContent = currentCaseIndex + 1;
    
    // Load next case
    loadCase(currentCaseIndex);
}

// Add badge to the badges container
function addBadge(badgeName, layerNumber) {
    const badge = document.createElement('div');
    badge.className = 'badge';
    badge.style.backgroundColor = getLayerColor(layerNumber);
    badge.textContent = 'L' + layerNumber;
    badge.title = badgeName;
    badgesContainer.appendChild(badge);
}

// Get color for layer
function getLayerColor(layerNumber) {
    const colors = {
        1: 'var(--layer1-color)',
        2: 'var(--layer2-color)',
        3: 'var(--layer3-color)',
        4: 'var(--layer4-color)',
        5: 'var(--layer5-color)',
        6: 'var(--layer6-color)',
        7: 'var(--layer7-color)'
    };
    
    return colors[layerNumber] || 'var(--primary-color)';
}

// End the game
function endGame() {
    // Hide game area
    document.querySelector('.game-area').style.display = 'none';
    
    // Show results
    document.querySelector('.game-results').style.display = 'block';
    
    // Update final score
    finalScoreElement.textContent = score;
    maxScoreElement.textContent = gameCases.length;
    
    // Display earned badges
    finalBadgesContainer.innerHTML = '';
    if (earnedBadges.length === 0) {
        finalBadgesContainer.innerHTML = '<p>No badges earned yet. Try again!</p>';
    } else {
        gameCases.forEach(gameCase => {
            if (earnedBadges.includes(gameCase.badge)) {
                const badge = document.createElement('div');
                badge.className = 'badge';
                badge.style.backgroundColor = getLayerColor(gameCase.correctLayer);
                badge.textContent = 'L' + gameCase.correctLayer;
                
                const badgeContainer = document.createElement('div');
                badgeContainer.className = 'badge-with-label';
                badgeContainer.appendChild(badge);
                
                const label = document.createElement('div');
                label.className = 'badge-label';
                label.textContent = gameCase.badge;
                badgeContainer.appendChild(label);
                
                finalBadgesContainer.appendChild(badgeContainer);
            }
        });
    }
}

// Restart the game
function restartGame() {
    // Hide results
    document.querySelector('.game-results').style.display = 'none';
    
    // Show game area
    document.querySelector('.game-area').style.display = 'block';
    
    // Start new game
    startGame();
}

// Add CSS for badges with labels
document.addEventListener('DOMContentLoaded', function() {
    const style = document.createElement('style');
    style.textContent = `
        .badge-with-label {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: 0 10px;
        }
        
        .badge-label {
            margin-top: 5px;
            font-size: 12px;
            text-align: center;
        }
    `;
    document.head.appendChild(style);
});
